import { Component } from '@theme/component';

/**
 * A custom element that gates purchase actions behind a confirmation checkbox.
 *
 * Until the checkbox is ticked, the add to cart button (and the sticky bar's
 * button, and the accelerated checkout buttons) are marked as unavailable and
 * any attempt to use them is blocked.
 *
 * The buttons keep `aria-disabled="true"` rather than the native `disabled`
 * attribute so they stay focusable and screen reader users can reach them and
 * hear why they cannot be used yet.
 *
 * Clicks are intercepted on `document` in the capture phase so that:
 *  - the theme's own declarative `on:click` bindings never run, and
 *  - the sticky bar is covered too, since sticky-add-to-cart.js adds to cart by
 *    programmatically clicking the main add to cart button.
 *
 * @extends {Component<{ checkbox: HTMLInputElement, liveRegion: HTMLElement }>}
 */
class CompatibilityGate extends Component {
  requiredRefs = ['checkbox', 'liveRegion'];

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {MutationObserver | null} */
  #observer = null;

  /** @type {number | null} */
  #syncFrame = null;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    // Capture phase, so this runs before any bubbling-phase handler the theme
    // attached to the buttons themselves.
    document.addEventListener('click', this.#handleCaptureClick, { capture: true, signal });
    document.addEventListener('submit', this.#handleCaptureSubmit, { capture: true, signal });

    // The product form and sticky bar are replaced wholesale on variant change,
    // which drops the state #sync() applied. Watch for inserted nodes only -
    // not attributes - so the writes #sync() makes cannot retrigger this.
    this.#observer = new MutationObserver(this.#scheduleSync);
    this.#observer.observe(document.body, { childList: true, subtree: true });

    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#abortController?.abort();
    this.#abortController = null;
    this.#observer?.disconnect();
    this.#observer = null;
    if (this.#syncFrame !== null) cancelAnimationFrame(this.#syncFrame);
    this.#syncFrame = null;
  }

  /** Coalesces re-syncs to one per frame. */
  #scheduleSync = () => {
    if (this.#syncFrame !== null) return;
    this.#syncFrame = requestAnimationFrame(() => {
      this.#syncFrame = null;
      this.#sync();
    });
  };

  /** @returns {boolean} Whether the shopper has confirmed compatibility. */
  get confirmed() {
    return Boolean(this.refs.checkbox?.checked);
  }

  /** Called from the checkbox via `on:change`. */
  handleChange = () => {
    this.#sync();

    if (this.confirmed) {
      this.#announce('');
    }
  };

  /**
   * The purchase controls this gate governs. Resolved on each use because the
   * product form and sticky bar can both be re-rendered on variant change.
   * @returns {HTMLElement[]}
   */
  #gatedButtons() {
    const selectors = [
      'product-form-component [ref="addToCartButton"]',
      'sticky-add-to-cart [ref="addToCartButton"]',
    ];

    return selectors.flatMap((selector) => Array.from(document.querySelectorAll(selector)));
  }

  /** @returns {HTMLElement[]} Accelerated checkout containers, hidden the same way the theme hides them when sold out. */
  #acceleratedContainers() {
    return Array.from(document.querySelectorAll('.accelerated-checkout-block'));
  }

  /** Applies the current state to every gated control. */
  #sync() {
    const confirmed = this.confirmed;

    for (const button of this.#gatedButtons()) {
      // Never re-enable a button the theme disabled for its own reasons
      // (sold out, unavailable) - that state is owned by the product form.
      if (button.hasAttribute('disabled')) continue;

      if (confirmed) {
        button.removeAttribute('aria-disabled');
        delete button.dataset.compatGated;
        button.removeAttribute('aria-describedby');
      } else {
        button.setAttribute('aria-disabled', 'true');
        button.dataset.compatGated = 'true';
        button.setAttribute('aria-describedby', this.refs.checkbox.id);
      }
    }

    for (const container of this.#acceleratedContainers()) {
      if (confirmed) {
        container.removeAttribute('inert');
        container.hidden = container.dataset.compatGatedHidden === 'true' ? false : container.hidden;
        delete container.dataset.compatGatedHidden;
      } else if (!container.hidden) {
        // Remember that we were the one who hid it, so we don't reveal a
        // container the theme hid because the variant is unavailable.
        container.dataset.compatGatedHidden = 'true';
        container.hidden = true;
        container.setAttribute('inert', '');
      }
    }
  }

  /**
   * Blocks clicks on gated controls.
   * @param {MouseEvent} event
   */
  #handleCaptureClick = (event) => {
    if (this.confirmed) return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    // Resolved live rather than trusting data-compat-gated: a button inserted
    // since the last sync has no attribute yet but must still be blocked.
    const button = this.#gatedButtons().find((el) => el === target || el.contains(target));
    if (!button) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.#reject();
  };

  /**
   * Blocks form submission (for example, pressing Enter in the form).
   * @param {SubmitEvent} event
   */
  #handleCaptureSubmit = (event) => {
    if (this.confirmed) return;

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.dataset.type !== 'add-to-cart-form') return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.#reject();
  };

  /** Moves attention to the checkbox and explains why nothing happened. */
  #reject() {
    this.#announce(this.dataset.blockedMessage || 'Please confirm your bike is compatible before adding to cart.');

    this.dataset.compatAttention = 'true';
    setTimeout(() => delete this.dataset.compatAttention, 1200);

    this.refs.checkbox.focus();
  }

  /**
   * @param {string} message
   */
  #announce(message) {
    this.refs.liveRegion.textContent = message;
  }
}

if (!customElements.get('compatibility-gate')) {
  customElements.define('compatibility-gate', CompatibilityGate);
}
